import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { listStaggerAnimation } from '@examdojo/animation';
import { SyllabusQuery } from '@examdojo/category';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { QuestionItem, STEM_DIFFICULTIES, StemDifficultyCategory } from '@examdojo/models/question';
import { PlatformService } from '@examdojo/platform';
import { PracticeActivityCandidateStoreModel } from '@examdojo/practice-activity';
import {
  calculateQuestionTotalMarks,
  DEFAULT_DIFFICULTY,
  QuestionEntityQuery,
  QuestionEntityService,
} from '@examdojo/question';
import { assertNonNullable } from '@examdojo/util/assert';
import { IonContent, IonHeader, IonTitle, IonToolbar, NavController } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, debounceTime, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { SwiperOptions } from 'swiper/types';
import { FooterPanelComponent } from '@examdojo/ui/footer-panel';
import { StoryCharacter } from '../../../../shared/sensei/character.model';
import { SenseiComponent } from '../../../../shared/sensei/sensei.component';
import { QuestionSelectionCardComponent } from './question-selection-card/question-selection-card.component';

@Component({
  selector: 'dojo-question-selection',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './question-selection.component.html',
  styleUrls: ['./question-selection.component.scss'],
  animations: [listStaggerAnimation('.question-selection-card')],
  imports: [
    TranslocoPipe,
    QuestionSelectionCardComponent,
    FooterPanelComponent,
    IonContent,
    SenseiComponent,
    IconButtonComponent,
    IonHeader,
    IonTitle,
    IonToolbar,
  ],
  host: {
    class: 'flex h-full flex-col overflow-hidden justify-between',
  },
})
export class QuestionSelectionComponent {
  constructor(
    private readonly navCtrl: NavController,
    private readonly route: ActivatedRoute,
    private readonly platformService: PlatformService,
    private readonly questionService: QuestionEntityService,
    private readonly questionQuery: QuestionEntityQuery,
    private readonly syllabusQuery: SyllabusQuery,
  ) {
    // Set initial selected candidate when questionCandidates input changes
    toObservable(this.questionCandidates)
      .pipe(
        filter((candidates) => candidates.length > 0),
        tap((candidates) => {
          this.selectedCandidateId.set(candidates[0].id);
          this.isSubmitted.set(false);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly activeStoryCharacter: StoryCharacter = {
    file: 'assets/images/senpei/thinking_senpai.riv',
    artboard: 'Artboard',
    stateMachine: 'State Machine 1',
    isBackground: false,
    isCentered: false,
    customStyles: {
      width: 'initial',
      height: '100%',
      objectFit: 'contain',
      position: 'relative',
      top: '0px',
      left: '0',
      'margin-left': '-20px',
    },
  };

  readonly currentQuestionProficiencyGain$ = computed(() => {
    const candidateId = this.selectedCandidateId();
    const questionCandidate = this.questionCandidates().find((candidate) => candidate.id === candidateId);

    if (!questionCandidate) {
      return null;
    }

    if (!questionCandidate.metadata) {
      return '0';
    }

    return (
      ((questionCandidate.metadata.predicted_score - questionCandidate.metadata.prior_predicted_score) /
        questionCandidate.metadata.prior_predicted_score) *
      100
    ).toFixed(2);
  });

  readonly activeSpeechBubble = computed(() => {
    const gain = this.currentQuestionProficiencyGain$();
    return {
      text: gain
        ? `If you outperform the baseline, your predicted score is going to increase by ${gain}%.`
        : `Select question, based on our predicted score impact`,
      translationParams: {},
    };
  });

  readonly selectedCandidateId = signal<PracticeActivityCandidateStoreModel['id'] | null>(null);

  readonly questionCandidates = input.required<PracticeActivityCandidateStoreModel[]>();

  readonly isSubmitted = signal(false);

  private readonly questionCandidatesWithItems$: Observable<
    Array<
      PracticeActivityCandidateStoreModel & {
        questionItems: QuestionItem[] | undefined;
        difficulty: StemDifficultyCategory;
      }
    >
  > = toObservable(this.questionCandidates).pipe(
    switchMap((questionCandidates) => {
      if (!questionCandidates.length) {
        return of([]);
      }

      const questionIds = questionCandidates.map((candidate) => candidate.question_id as number);

      return this.questionService.fetchByIds(questionIds).pipe(
        map((entities) =>
          entities.map((entity) => this.questionQuery.toUIModelFn({ question: entity, id: entity.id })),
        ),
        map((questionEntities) => {
          return questionCandidates.map((questionCandidate) => ({
            ...questionCandidate,
            questionItems: questionEntities.find((entity) => entity.id === questionCandidate.question_id)?.question
              ?.question_items,
            difficulty:
              questionEntities.find((entity) => entity.id === questionCandidate.question_id)?.question?.difficulty ??
              DEFAULT_DIFFICULTY,
          }));
        }),
      );
    }),
  );

  readonly candidateSelected = output<PracticeActivityCandidateStoreModel>();
  readonly cancelled = output<void>();

  readonly swiperOptions: SwiperOptions = {
    effect: 'cards',
    cardsEffect: {
      slideShadows: false,
    },
    grabCursor: true,
  };

  readonly disablePrev = signal<boolean>(true);
  readonly disableNext = signal<boolean>(false);

  readonly state = connectState({
    activeSpeechBubble: toObservable(this.activeSpeechBubble).pipe(debounceTime(0)),
    isMobile: this.platformService.isSmall$,
    sortedQuestionCandidates: combineLatest([
      this.questionCandidatesWithItems$,
      this.syllabusQuery.markCalculationMethod$,
    ]).pipe(
      map(([candidates, markCalculationMethod]) =>
        candidates
          .map((candidate) => ({
            ...candidate,
            totalMarks: candidate.questionItems
              ? calculateQuestionTotalMarks(candidate.questionItems, markCalculationMethod)
              : 0,
            difficulty: candidate.difficulty,
          }))
          .toSorted((a, b) => {
            const difficultyOrder = STEM_DIFFICULTIES;
            return difficultyOrder.indexOf(a.difficulty ?? 'EASY') - difficultyOrder.indexOf(b.difficulty ?? 'EASY');
          }),
      ),
    ),
  });

  selectCandidateCard(candidateId: string) {
    this.selectedCandidateId.set(candidateId);
  }

  submit() {
    const selectedCandidateId = this.selectedCandidateId();
    if (!selectedCandidateId || this.isSubmitted()) {
      return;
    }

    this.isSubmitted.set(true);

    const selectedCandidate = this.questionCandidates().find((candidate) => candidate.id === selectedCandidateId);
    assertNonNullable(selectedCandidate, 'selectedCandidate');

    this.candidateSelected.emit(selectedCandidate);
  }

  navigateToParent(): void {
    this.navCtrl.navigateBack(['../'], { relativeTo: this.route });
  }

  cancel() {
    this.cancelled.emit();
  }
}
